// When to show the install screen. Pure decision logic, no React and no
// storage, so the rules can be unit-tested and argued about on their own.
//
// The rule: once straight after the first successful login, once more on the
// fourth session, then never. Dismissal is respected — two "Not now"s and we
// stop asking. Nobody is nagged into installing.

export const MAX_DISMISSALS = 2;
export const SECOND_ASK_SESSION = 4;

/** The shape persisted in member_prefs.install_state (mirrored to localStorage). */
export const emptyInstallState = () => ({
  sessions: 0,
  dismissals: 0,
  shownAfterFirstLogin: false,
  shownAtSecondAsk: false,
  installed: false,
});

export function normaliseState(raw) {
  const base = emptyInstallState();
  if (!raw || typeof raw !== 'object') return base;
  return {
    sessions: Number.isFinite(raw.sessions) ? raw.sessions : base.sessions,
    dismissals: Number.isFinite(raw.dismissals) ? raw.dismissals : base.dismissals,
    shownAfterFirstLogin: Boolean(raw.shownAfterFirstLogin),
    shownAtSecondAsk: Boolean(raw.shownAtSecondAsk),
    installed: Boolean(raw.installed),
  };
}

/** Call once per app start, before asking shouldShowInstall. */
export function startSession(state) {
  const next = normaliseState(state);
  return { ...next, sessions: next.sessions + 1 };
}

/**
 * @param {object} state      persisted install state
 * @param {object} ctx
 * @param {boolean} ctx.standalone  already running as an installed app
 * @param {boolean} ctx.signedIn    there is a session
 * @returns {null | 'first-login' | 'second-ask'} which ask to show, if any
 */
export function shouldShowInstall(state, { standalone = false, signedIn = false } = {}) {
  const s = normaliseState(state);

  // Never pester someone who already installed, or who is reading this inside
  // the installed app.
  if (standalone || s.installed) return null;
  if (!signedIn) return null;
  if (s.dismissals >= MAX_DISMISSALS) return null;

  if (!s.shownAfterFirstLogin) return 'first-login';
  if (!s.shownAtSecondAsk && s.sessions >= SECOND_ASK_SESSION) return 'second-ask';
  return null;
}

export function markShown(state, reason) {
  const s = normaliseState(state);
  if (reason === 'first-login') return { ...s, shownAfterFirstLogin: true };
  if (reason === 'second-ask') return { ...s, shownAtSecondAsk: true };
  return s;
}

export function markDismissed(state) {
  const s = normaliseState(state);
  return { ...s, dismissals: s.dismissals + 1 };
}

export function markInstalled(state) {
  return { ...normaliseState(state), installed: true };
}
